/**
 * SplitCache - Cache utility for managing split variable tracking
 * Handles caching feature flag values in sessionStorage with optional key prefixing
 * for proper isolation between different users/sessions
 */
export class SplitCache {
  private static readonly CACHE_KEY = 'split_feature_flags_cache';

  /**
   * Gets the cached value for a specific feature flag
   * @param featureFlag The feature flag name
   * @param keyPrefix Optional key prefix to scope the cache to a specific user/session
   */
  static getCachedValue(featureFlag: string, keyPrefix?: string | null): string | null {
    try {
      const cache = sessionStorage.getItem(this.CACHE_KEY);
      if (!cache) return null;
      
      const parsedCache = JSON.parse(cache);
      const cacheKey = keyPrefix ? `${keyPrefix}:${featureFlag}` : featureFlag;
      const cachedValue = parsedCache[cacheKey];
      
      return cachedValue || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Sets the cached value for a specific feature flag
   * @param featureFlag The feature flag name
   * @param value The value to cache
   * @param keyPrefix Optional key prefix to scope the cache to a specific user/session
   */
  static setCachedValue(featureFlag: string, value: string, keyPrefix?: string | null): void {
    try {
      const cache = sessionStorage.getItem(this.CACHE_KEY);
      const parsedCache = cache ? JSON.parse(cache) : {};
      
      const cacheKey = keyPrefix ? `${keyPrefix}:${featureFlag}` : featureFlag;
      parsedCache[cacheKey] = value;
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(parsedCache));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Determines if tracking should occur based on cache comparison
   * Returns true if:
   * 1. No cached value exists (first time)
   * 2. Cached value exists but is different from current value
   * Returns false if:
   * 1. Current value is null/undefined
   * 2. Cached value is the same as current value
   * @param featureFlag The feature flag name
   * @param currentValue The current value to compare with cached value
   * @param keyPrefix Optional key prefix to scope the cache to a specific user/session
   */
  static shouldTrack(featureFlag: string, currentValue: string | null, keyPrefix?: string | null): boolean {
    // Don't track if current value is null or undefined
    if (!currentValue || currentValue === 'null' || currentValue === 'undefined') {
      return false;
    }

    const cachedValue = this.getCachedValue(featureFlag, keyPrefix);
    
    // Track if no cached value exists (first time)
    if (cachedValue === null) {
      return true;
    }

    // Track if cached value is different from current value
    if (cachedValue !== currentValue) {
      return true;
    }

    // Don't track if values are the same
    return false;
  }

  /**
   * Clear all cached values (useful for testing or reset)
   */
  static clearCache(): void {
    try {
      sessionStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all cached values (useful for debugging)
   */
  static getAllCachedValues(): Record<string, string> {
    try {
      const cache = sessionStorage.getItem(this.CACHE_KEY);
      return cache ? JSON.parse(cache) : {};
    } catch (error) {
      return {};
    }
  }
}
